import { CSSObject } from '@emotion/react'
import { fontWeights, fonts, fontSizes } from '@opallabs/pulsar'
import { colors } from '@opallabs/spectrum'
import { mediaControlsAtTop } from '../../../../helpers/styles'
import { postHeight, postWidth } from '../../styles'

export const defaultFooterHeight = 137

export const inputStyles: CSSObject = {
  alignItems: 'center',
  backgroundColor: 'transparent',
  boxSizing: 'border-box',
  color: colors.white.base,
  display: 'block',
  fontFamily: fonts['helvetica'],
  fontSize: fontSizes.medium,
  letterSpacing: '0.1px',
  lineHeight: '16px',
}

export const overlay = (truncate: boolean): CSSObject => ({
  color: colors.white.base,
  fontFamily: fonts['helvetica'],
  height: '100%',
  pointerEvents: 'none',
  position: 'absolute',
  width: '100%',
  zIndex: truncate ? 1 : 1001,
})

export const overlayHeader: CSSObject = {
  alignItems: 'start',
  background:
    'linear-gradient(180deg, rgba(0, 0, 0, 0.20) 0%, rgba(0, 0, 0, 0.00) 100%)',
  display: 'flex',
  justifyContent: 'space-between',
  padding: '18px 18px 0px 25px',
  pointerEvents: 'auto',
  height: 68,
}

export const headerLeftContainer: CSSObject = {
  alignItems: 'center',
  display: 'flex',
}

export const headerRightContainer: CSSObject = {
  display: 'flex',
  gap: 22,
  height: 22,
  marginTop: 2,
}

export const overlayTitle: CSSObject = {
  fontSize: '20px',
  fontWeight: fontWeights.bolder,
}

export const columnActionContainer: CSSObject = {
  alignItems: 'flex-end',
  display: 'flex',
  flexDirection: 'column',
  margin: '324px 17px 0px 0px',
  marginLeft: 'auto',
  position: 'relative',
  top: 4,
  zIndex: 99,
}

export const columnActionIcon = {
  marginBottom: 8,
}

export const iconNumberText: CSSObject = {
  fontSize: fontSizes.xSmall,
  letterSpacing: '0.09px',
  margin: '0px 2px 23px 0px',
}

export const footer = (readOnly: boolean): CSSObject => ({
  background: 'linear-gradient(-180deg, rgba(0,0,0,0), rgba(0,0,0,0.5) 100%)',
  bottom: 0,
  boxSizing: 'border-box',
  cursor: readOnly ? 'pointer' : 'default',
  paddingLeft: 25,
  pointerEvents: readOnly ? 'none' : 'auto',
  position: 'absolute',
  width: postWidth,
})

export const footerOptionsContainer: CSSObject = {
  alignItems: 'center',
  display: 'flex',
  gap: 6,
}

export const withCollab = 'with-collab'
const withCollabSelector = `.kuiper-${withCollab}`

export const account: CSSObject = {
  alignItems: 'center',
  color: colors.orange.base,
  display: 'flex',
  padding: '27px 68px 6px 0px',
}

export const avatarContainer: CSSObject = {
  flexShrink: 0,
  [`${withCollabSelector} &`]: {
    height: 35,
    position: 'relative',
    width: 35,
  },
}

export const collabAvatarSecondary: CSSObject = {
  backgroundColor: 'rgb(208, 218, 225)',
  border: '1px solid white',
  borderRadius: '50%',
  bottom: -2,
  position: 'absolute',
  right: -8,
}

export const accountNameWrapper: CSSObject = {
  color: colors.white.base,
  fontSize: fontSizes.medium,
  marginLeft: 8,
  maxWidth: 240,
  [`${withCollabSelector} &`]: {
    alignItems: 'baseline',
    display: 'flex',
    flexWrap: 'nowrap',
    marginLeft: 16,
    minWidth: 0,
    overflow: 'hidden',
  },
  [`${withCollabSelector} & .kuiper-account-name`]: {
    flex: '0 1 auto',
    maxWidth: 'none',
    minWidth: 0,
  },
}

export const accountName: CSSObject = {
  display: 'block',
  fontFamily: fonts.helvetica,
  fontWeight: fontWeights.bolder,
  maxWidth: 154,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
}

export const collabSeparator: CSSObject = {
  color: colors.white.base,
  flexShrink: 0,
  fontSize: fontSizes.medium,
  fontWeight: 'normal',
  whiteSpace: 'pre',
}

export const followButton: CSSObject = {
  alignItems: 'center',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  borderRadius: 5,
  display: 'inline-flex',
  height: 23,
  width: 59,

  span: {
    color: colors.white.base,
    fontSize: fontSizes.medium,
    fontWeight: fontWeights.normal,
    letterSpacing: '0.1px',
    margin: 'auto',
  },
}

export const optionIcon = {
  paddingRight: 4,
}

export const globeIcon: CSSObject = {
  color: colors.white.base,
  display: 'flex',
  padding: '2px 4px',
}

export const optionTextContainer = (readOnly: boolean): CSSObject => ({
  alignItems: 'center',
  backgroundColor: 'rgba(0,0,0,0.5)',
  borderRadius: 24,
  display: 'inline-flex',
  height: 25,
  marginBottom: 14,
  marginTop: 2,
  maxWidth: 126,
  padding: '0px 8px',

  '&:hover': {
    background: readOnly ? undefined : 'rgba(0,0,0,0.3)',
  },

  input: {
    ...inputStyles,
    border: 'none',
    fontSize: fontSizes.xxSmall,
    maxWidth: 106,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    '&::placeholder': {
      color: colors.white.base,
    },
    '&:active, &:focus': {
      border: 'none',
      outline: 'none',
    },
  },
})

export const contentAsset: CSSObject = {
  backgroundColor: '#000',
  height: postHeight.mobileSingle,
  video: {
    height: postHeight.mobileSingle,
    width: postWidth,
  },
}

export const contentAssetBase = (
  showDescription: boolean,
  footerHeight: number,
  truncate: boolean
): CSSObject => ({
  alignItems: 'center',
  backgroundColor: showDescription ? '#000' : colors.n8.base,
  borderRadius: 2,
  display: 'flex',
  height: postHeight.mobileSingle,
  opacity: showDescription ? 0.2 : 1,

  ...mediaControlsAtTop,
  '.kuiper-asset-info-wrapper': {
    marginBottom: footerHeight < 240 || truncate ? 0 : 120,
  },
})

export const captionContainer = (
  nSoundAndLocation: boolean,
  readOnly: boolean
): CSSObject => ({
  marginBottom: 4,
  paddingBottom: nSoundAndLocation && readOnly ? 34 : undefined,
})

export const captionPlaceholder: CSSObject = {
  color: `${colors.white.base} !important`,
}

export const caption = (truncate: boolean, readOnly: boolean): CSSObject => ({
  ...inputStyles,
  width: 292,

  '&:hover': {
    background: truncate || readOnly ? 'transparent' : 'rgba(0,0,0,0.2)',
  },
})
